import React, {useState, useCallback, useEffect} from 'react';
import analytics from '@react-native-firebase/analytics';
import {ScrollView, ActivityIndicator, RefreshControl} from 'react-native';
import {
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import {useBottomTabBarHeight} from '@react-navigation/bottom-tabs';

import {useAuth} from '../../context/auth';
import {useEmprestimoAlerts} from '../../context/EmprestimoAlerts';
import {EmprestimoItemAPI, PropostaItemAPI} from '../../services/creditoMova';
import {PropostaDTO} from '../../dtos/EmprestimoDTO';

import {EmprestimoActionCard} from '../../components/Interface/EmprestimoActionCard';
import {EmprestimoTermsModal} from '../../components/shared/EmprestimoTermsModal';
import {EmprestimoEligibilityGate} from '../../components/shared/EmprestimoEligibilityGate';
import {ProfileHeader} from '../../components/Interface/Headers/ProfileHeader';
import {ScreenTitle} from '../../components/shared/ScreenTitle';
import {
  HeaderIconButton,
  HeaderIconButtonsRow,
} from '../../components/Interface/Headers/HeaderIconButton';

import {ParcelasBottomSheet} from './components/ParcelasBottomSheet';
import {PropostaItemCard} from './components/PropostaItemCard';
import {EmprestimoItemCard} from './components/EmprestimoItemCard';
import {formatDateBR} from '../../utils/format';

import bannerSimulacao from '../../assets/images/banners/emprestimo-simulacao.webp';
import bannerDinheiroExtra from '../../assets/images/banners/emprestimo-dinheiro-extra.webp';
import bannerAprovado from '../../assets/images/banners/emprestimo-aprovado.webp';

import {
  Container,
  EmptyStateContainer,
  EmptyStateIconWrapper,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  AlertBanner,
  AlertBannerIcon,
  AlertBannerText,
  AlertBannerBold,
  TabRow,
  TabContainer,
  TabButton,
  TabLabel,
  TabLabelRow,
  TabBadge,
  TabBadgeText,
} from './styles';

import {rastrear, STATUS_BY_TIPO_LISTAGEM} from '../../services/rastrear';

type TabType = 'propostas' | 'emprestimos';

export function Emprestimo() {
  const navigation = useNavigation();
  const route = useRoute();
  const tabBarHeight = useBottomTabBarHeight();
  const {user} = useAuth();

  const params = route.params as
    | {initialTab?: TabType; openParcelasForOperacaoId?: string}
    | undefined;
  const initialTab = params?.initialTab ?? 'emprestimos';
  const openParcelasForOperacaoId = params?.openParcelasForOperacaoId ?? null;

  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [showEmprestimoTerms, setShowEmprestimoTerms] = useState(false);
  const [parcelasEmprestimo, setParcelasEmprestimo] =
    useState<EmprestimoItemAPI | null>(null);

  const emprestimoBanners = [
    {
      id: 1,
      imageUrl: bannerSimulacao,
      action: () => navigation.navigate('Simular'),
    },
    {
      id: 2,
      imageUrl: bannerDinheiroExtra,
      action: () => navigation.navigate('Simular'),
    },
    {
      id: 3,
      imageUrl: bannerAprovado,
      action: () => navigation.navigate('Simular'),
    },
  ];

  const userName = user?.name?.trim().split(' ')[0];
  const titleText = userName
    ? `Olá, ${userName}! Faça uma simulação de empréstimo!`
    : 'Olá! Faça uma simulação de empréstimo!';

  const {
    propostas: propostasAPI,
    emprestimos: emprestimosAPI,
    parcelasWarningMap,
    loading,
    alertType,
    alertOperacaoId,
    loadData,
  } = useEmprestimoAlerts();

  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, [loadData]);

  useFocusEffect(
  useCallback(() => {
    loadData();
    rastrear({status_tela: STATUS_BY_TIPO_LISTAGEM.EP});
    analytics().logEvent('emprestimo_tela_inicial');
  }, [loadData]),
);

  useEffect(() => {
    if (!openParcelasForOperacaoId || loading) {
      return;
    }
    const item = emprestimosAPI.find(
      e => e.operacao_id === openParcelasForOperacaoId,
    );
    if (item) {
      setActiveTab('emprestimos');
      setParcelasEmprestimo(item);
      navigation.setParams({openParcelasForOperacaoId: undefined});
    }
  }, [loading, emprestimosAPI, openParcelasForOperacaoId, navigation]);

  const handlePropostaPress = (item: PropostaItemAPI) => {
    if (item.next_action === 'assinar') {
      const proposta: PropostaDTO = {
        id: item.operacao_id,
        operacao_id: item.operacao_id,
        valor: item.valor_solicitado_centavos / 100,
        parcelas: item.prazo_meses,
        valor_parcela: item.valor_contratado_centavos / 100 / item.prazo_meses,
        taxa_mensal: 0,
        total: item.valor_contratado_centavos / 100,
        status: 'assinatura',
        statusText: 'Em assinatura',
        data_criacao: formatDateBR(item.created_at),
        proposal_id: item.mova_proposal_id,
      };
      navigation.navigate('EmprestimoAssinatura', {proposta});
    } else if (item.next_action === 'continuar') {
      navigation.navigate('EmprestimoSimulacao', {
        prefill_valor_centavos: item.valor_solicitado_centavos,
        prefill_prazo_meses: item.prazo_meses,
      });
    } else if (item.next_action === 'reprovada') {
      navigation.navigate('EmprestimoResultadoRecusado');
    }
  };

  const sortedEmprestimos = [...emprestimosAPI].sort((a, b) => {
    const priority = (w: (typeof parcelasWarningMap)[string]) => {
      if (w?.status === 'em_atraso') {
        return 0;
      }
      if (w?.status === 'vence_hoje' || w?.status === 'a_vencer') {
        return 1;
      }
      return 2;
    };
    return (
      priority(parcelasWarningMap[a.operacao_id]) -
      priority(parcelasWarningMap[b.operacao_id])
    );
  });

  return (
    <>
      <EmprestimoEligibilityGate />

      <EmprestimoTermsModal
        visible={showEmprestimoTerms}
        onClose={() => setShowEmprestimoTerms(false)}
        onAccept={() => {
          setShowEmprestimoTerms(false);
          navigation.navigate('EmprestimoSimulacao');
        }}
      />

      <ParcelasBottomSheet
        visible={parcelasEmprestimo !== null}
        onDismiss={() => setParcelasEmprestimo(null)}
        emprestimo={parcelasEmprestimo}
      />

      <ProfileHeader />


      <Container>
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{paddingBottom: tabBarHeight + 24}}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }>
                 <ScreenTitle
        title="Empréstimo"
        rightContent={
          <HeaderIconButtonsRow>
            <HeaderIconButton icon="refresh" onPress={handleRefresh} />
          </HeaderIconButtonsRow>
        }
      />

         <EmprestimoActionCard
  title={titleText}
  limitValue="R$ 400,00"
  actionLabel="Simular Agora"
  onActionPress={() => {
    analytics().logEvent('emprestimo_clicou_simular');
    setShowEmprestimoTerms(true);
  }}
  banners={emprestimoBanners}
  statementLabel="Ver extrato"
  onStatementPress={() => navigation.navigate('EmprestimoExtrato')}
  onRefresh={handleRefresh}
  refreshing={refreshing}
/>

          {alertType === 'vencidas' && alertOperacaoId && (
            <AlertBanner
              color="red"
              activeOpacity={0.8}
              onPress={() => setActiveTab('emprestimos')}>
              <AlertBannerIcon color="red" name="alert-circle" />
              <AlertBannerText>
                Você possui{' '}
                <AlertBannerBold color="red">
                  parcelas em atraso
                </AlertBannerBold>
                {'. Regularize para evitar encargos.'}
              </AlertBannerText>
            </AlertBanner>
          )}

          {alertType === 'a_vencer' && alertOperacaoId && (
            <AlertBanner
              color="orange"
              activeOpacity={0.8}
              onPress={() => setActiveTab('emprestimos')}>
              <AlertBannerIcon color="orange" name="clock" />
              <AlertBannerText>
                Você possui{' '}
                <AlertBannerBold color="orange">
                  parcelas próximas do vencimento
                </AlertBannerBold>
                {'. Fique em dia e evite juros.'}
              </AlertBannerText>
            </AlertBanner>
          )}

          {alertType === 'assinatura' && (
            <AlertBanner
              color="blue"
              activeOpacity={0.8}
              onPress={() => setActiveTab('emprestimos')}>
              <AlertBannerIcon color="blue" name="file-text" />
              <AlertBannerText>
                Você tem{' '}
                <AlertBannerBold color="blue">
                  proposta pronta para assinar
                </AlertBannerBold>
                {'. Conclua sua contratação!'}
              </AlertBannerText>
            </AlertBanner>
          )}

          <TabRow>
            <TabContainer>
              <TabButton
                active={activeTab === 'emprestimos'}
                onPress={() => setActiveTab('emprestimos')}
                activeOpacity={0.8}>
                <TabLabelRow>
                  <TabLabel active={activeTab === 'emprestimos'}>
                    Empréstimos
                  </TabLabel>
                  {emprestimosAPI.length > 0 && (
                    <TabBadge active={activeTab === 'emprestimos'}>
                      <TabBadgeText>{emprestimosAPI.length}</TabBadgeText>
                    </TabBadge>
                  )}
                </TabLabelRow>
              </TabButton>

              <TabButton
                active={activeTab === 'propostas'}
                onPress={() => setActiveTab('propostas')}
                activeOpacity={0.8}>
                <TabLabelRow>
                  <TabLabel active={activeTab === 'propostas'}>
                    Propostas
                  </TabLabel>
                  {propostasAPI.filter(i => i.status !== 'simulacao_disponivel')
                    .length > 0 && (
                    <TabBadge active={activeTab === 'propostas'}>
                      <TabBadgeText>
                        {
                          propostasAPI.filter(
                            i => i.status !== 'simulacao_disponivel',
                          ).length
                        }
                      </TabBadgeText>
                    </TabBadge>
                  )}
                </TabLabelRow>
              </TabButton>
            </TabContainer>
          </TabRow>

          {/* ─── Tab: Propostas ─── */}
          {activeTab === 'propostas' && (
            <>
              {loading && (
                <EmptyStateContainer>
                  <ActivityIndicator size="large" />
                </EmptyStateContainer>
              )}

              {!loading &&
                propostasAPI.filter(i => i.status !== 'simulacao_disponivel')
                  .length === 0 && (
                  <EmptyStateContainer>
                    <EmptyStateIconWrapper>
                      <EmptyStateIcon name="send" />
                    </EmptyStateIconWrapper>
                    <EmptyStateTitle>Nenhuma proposta enviada</EmptyStateTitle>
                    <EmptyStateText>
                      Faça uma simulação e envie sua{'\n'}proposta de crédito!
                    </EmptyStateText>
                  </EmptyStateContainer>
                )}

              {!loading &&
                propostasAPI
                  .filter(item => item.status !== 'simulacao_disponivel')
                  .map(item => (
                    <PropostaItemCard
                      key={item.operacao_id}
                      item={item}
                      onPress={() => handlePropostaPress(item)}
                    />
                  ))}
            </>
          )}

          {/* ─── Tab: Empréstimos ─── */}
          {activeTab === 'emprestimos' && (
            <>
              {loading && (
                <EmptyStateContainer>
                  <ActivityIndicator size="large" />
                </EmptyStateContainer>
              )}

              {!loading && emprestimosAPI.length === 0 && (
                <EmptyStateContainer>
                  <EmptyStateIconWrapper>
                    <EmptyStateIcon name="file-text" />
                  </EmptyStateIconWrapper>
                  <EmptyStateTitle>Nenhum empréstimo ainda</EmptyStateTitle>
                  <EmptyStateText>
                    Faça uma simulação e descubra as{'\n'}melhores condições
                    para você!
                  </EmptyStateText>
                </EmptyStateContainer>
              )}

              {!loading &&
                sortedEmprestimos.map(item => (
                  <EmprestimoItemCard
                    key={item.operacao_id}
                    item={item}
                    warning={parcelasWarningMap[item.operacao_id] ?? null}
                    onVerParcelas={() => setParcelasEmprestimo(item)}
                  />
                ))}
            </>
          )}
        </ScrollView>
      </Container>
    </>
  );
}
